import { useMemo, useState, type ReactNode } from 'react';
import { MessageSquare, Phone } from 'lucide-react';
import { formatPhone, telHref } from '../widget-lib/phone-tel';
import { useConfirm } from '../widget-ui/confirm-dialog';
import { formatDayRangeShort, formatSlotDayShort } from '../widget-lib/format-date';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../widget-api/http-client';
import {
  deleteScheduleSlot,
  fetchTrainerManageSlots,
  fetchTrainerManageSlotsAllBranches,
  type ScheduleSlotListItem
} from '../widget-api/schedule-slots';
import { TrainerScheduleStaffDialog, type TrainerScheduleStaffDialogMode } from './trainer_TrainerScheduleStaffDialog';
import {
  deleteStaffBooking,
  listSlotBookings,
  setStaffBookingAttendance,
  type BookingAttendance,
  type StaffBookingListItem
} from '../widget-api/trainer-clients';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { getMe } from '../widget-api/auth-me';
import { postSelectTrainerBranch } from '../widget-api/trainer-branch';
import { clearEmbedSession, clearGlobalEmbedSession, getEmbedSession, setEmbedSession } from '../widget-lib/embed-session-store';
import { useAsync } from '../widget-hooks/useAsync';
import { useTrainerBranchOptions } from '../widget-hooks/useTrainerBranchOptions';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { hexToRgba, resolveSlotAccent } from '../widget-lib/slot-color';
import {
  isTrainerOwnOrUnassignedSlot,
  isTrainerOwnSlot,
  pickActiveBookingSlots
} from '../widget-lib/trainer-slot-utils';
import {
  TrainerStudioGrid,
  type TrainerStudioSpaceFilter
} from './trainer_TrainerStudioGrid';
import { PATH } from '../widget-lib/path-routes';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';

export type TrainerMobileSlotsLayout = 'today' | 'week';

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function toLocalIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIsoToLocalDate(iso: string): Date {
  const parts = iso.split('-');
  const y = Number.parseInt(parts[0] ?? '', 10);
  const mo = Number.parseInt(parts[1] ?? '', 10);
  const da = Number.parseInt(parts[2] ?? '', 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(da)) {
    return new Date();
  }
  return new Date(y, mo - 1, da, 12, 0, 0, 0);
}

function normalizeTimeForParse(t: string): string {
  const s = (t || '').trim();
  if (s.length >= 8) return s.slice(0, 8);
  if (s.length === 5) return `${s}:00`;
  return '00:00:00';
}

function slotBounds(slot: ScheduleSlotListItem): { start: Date; end: Date } {
  const start = new Date(`${slot.slot_date}T${normalizeTimeForParse(slot.start_time)}`);
  const end = new Date(`${slot.slot_date}T${normalizeTimeForParse(slot.end_time)}`);
  return { start, end };
}

function groupByDate(slots: ScheduleSlotListItem[]): Map<string, ScheduleSlotListItem[]> {
  const map = new Map<string, ScheduleSlotListItem[]>();
  const sorted = [...slots].sort((a, b) => {
    const da = `${a.slot_date}T${a.start_time}`;
    const db = `${b.slot_date}T${b.start_time}`;
    return da.localeCompare(db);
  });
  for (const slot of sorted) {
    const list = map.get(slot.slot_date) ?? [];
    list.push(slot);
    map.set(slot.slot_date, list);
  }
  return map;
}

function formatDayLabel(isoDate: string): string {
  try {
    const date = parseIsoToLocalDate(isoDate);
    return date.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch {
    return isoDate;
  }
}



type Props = {
  heading: string;
  description: string;
  trainerMobileLayout?: TrainerMobileSlotsLayout | null;
  /** Создание/изменение/перенос/уведомление/удаление слотов — право `schedule:staff_write` (бэкенд `PermScheduleStaff`). */
  canStaffScheduleWrite?: boolean;
};

/** Время ЧЧ:ММ по часам устройства из ISO-метки. */
function formatClockTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function bookingClientLabel(booking: StaffBookingListItem): string {
  return booking.client_name?.trim() || `Клиент #${booking.client_id ?? booking.id}`;
}

function sessionsWordRu(n: number): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return 'тренировка';
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 > 20)) return 'тренировки';
  return 'тренировок';
}

function fillRatio(slot: ScheduleSlotListItem): number {
  if (slot.max_participants <= 0) return 0;
  const taken = Math.max(0, slot.max_participants - slot.places_left);
  return Math.min(1, taken / slot.max_participants);
}

function slotStatus(slot: ScheduleSlotListItem, now: Date): 'upcoming' | 'active' | 'done' {
  const { start, end } = slotBounds(slot);
  if (now < start) return 'upcoming';
  if (now < end) return 'active';
  return 'done';
}

type TrainerMobileTodayProps = {
  slots: ScheduleSlotListItem[];
  suffix: string;
  branchLabel: string | null;
  reload: () => void;
};

function TrainerMobileTodayLayout({
  slots,
  suffix,
  branchLabel,
  reload,
  renderSlotBookings
}: TrainerMobileTodayProps & { renderSlotBookings: (slot: ScheduleSlotListItem, compact?: boolean) => ReactNode }) {
  const navigate = useNavigate();
  const todayIso = toLocalIsoDate(new Date());
  const todaySlots = useMemo(
    () =>
      (slots ?? [])
        .filter((s) => s.slot_date === todayIso)
        .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? '')),
    [slots, todayIso]
  );
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const doneCount = todaySlots.filter((s) => slotStatus(s, new Date()) === 'done').length;
  const total = todaySlots.length;

  const nextAfterToday = useMemo(() => {
    const at = new Date();
    const sorted = [...(slots ?? [])].sort((a, b) => {
      const da = `${a.slot_date}T${a.start_time ?? ''}`;
      const db = `${b.slot_date}T${b.start_time ?? ''}`;
      return da.localeCompare(db);
    });
    const first = sorted.find((s) => {
      const { start } = slotBounds(s);
      return start.getTime() > at.getTime() && s.slot_date !== todayIso;
    });
    if (!first) return null;
    const t = (first.start_time || '').slice(0, 5);
    try {
      const when = parseIsoToLocalDate(first.slot_date).toLocaleDateString('ru-RU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
      return { label: `${when} в ${t}`, slot: first };
    } catch {
      return { label: `${formatSlotDayShort(first.slot_date)} в ${t}`, slot: first };
    }
  }, [slots, todayIso]);

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (total === 0) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-[color-mix(in_srgb,var(--border)_55%,transparent)] bg-[color-mix(in_srgb,var(--bg-elevated)_88%,transparent)] px-4 py-4 shadow-[var(--shadow-card)] backdrop-blur-md">
          <p className="text-sm text-[var(--text-muted)]">{formatDayLabel(todayIso)}</p>
          <p className="mt-1 text-2xl font-bold tracking-tight text-[var(--text)]">Сегодня тренировок нет</p>
        </div>
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/80 px-4 py-10 text-center">
          <span className="text-4xl opacity-40 grayscale" aria-hidden>
            🏋
          </span>
          <p className="text-sm text-[var(--text-muted)]">
            {nextAfterToday ? (
              <>
                Ближайшая — {nextAfterToday.label}
              </>
            ) : (
              <>В ближайшие две недели слотов не запланировано.</>
            )}
          </p>
          <button
            type="button"
            className="text-sm font-semibold text-[var(--accent)] underline-offset-2 hover:underline"
            onClick={() => navigate(`${PATH.trainerWorkSchedule}${suffix}`)}
          >
            Смотреть расписание →
          </button>
        </div>
        <p className="text-center text-xs text-[var(--text-muted)]">
          <button type="button" className="text-[var(--accent)] underline-offset-2 hover:underline" onClick={() => reload()}>
            Обновить
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[color-mix(in_srgb,var(--border)_55%,transparent)] bg-[color-mix(in_srgb,var(--bg-elevated)_88%,transparent)] px-4 py-4 shadow-[var(--shadow-card)] backdrop-blur-md">
        <p className="text-sm text-[var(--text-muted)]">{formatDayLabel(todayIso)}</p>
        <p className="mt-1 text-2xl font-bold tracking-tight text-[var(--text)]">
          У вас {total} {sessionsWordRu(total)} сегодня
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5" aria-hidden>
            {todaySlots.map((s) => {
              const st = slotStatus(s, new Date());
              return (
                <span
                  key={s.id}
                  className="h-2.5 w-2.5 rounded-full"
                  style={{
                    backgroundColor:
                      st === 'done'
                        ? 'color-mix(in srgb, var(--text-muted) 55%, transparent)'
                        : st === 'active'
                          ? 'var(--accent)'
                          : 'color-mix(in srgb, var(--accent) 45%, transparent)'
                  }}
                />
              );
            })}
          </div>
          <span className="text-xs text-[var(--text-muted)]">
            {doneCount} из {total} прошло
          </span>
        </div>
      </div>

      <ul className="space-y-2.5">
        {todaySlots.map((slot) => {
          const accentHex = resolveSlotAccent(slot.service_type_color, slot.service_color);
          const stripe = accentHex || 'var(--accent)';
          const st = slotStatus(slot, new Date());
          const ratio = fillRatio(slot);
          const pct = Math.round(ratio * 100);
          const time = (slot.start_time || '').slice(0, 5);
          const taken = Math.max(0, slot.max_participants - slot.places_left);
          const isOpen = expanded.has(slot.id);
          const pulse = st === 'active';

          return (
            <li
              key={slot.id}
              className={`overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--border)_60%,transparent)] bg-[var(--bg-elevated)] shadow-sm ${
                st === 'done' ? 'opacity-80' : ''
              }`}
            >
              <div className="flex">
                <div className="w-1 shrink-0 rounded-l-2xl" style={{ backgroundColor: stripe }} />
                <div className="min-w-0 flex-1 px-3 py-2.5">
                  <button type="button" className="w-full text-left" onClick={() => toggleExpand(slot.id)}>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span
                        className={`text-base font-bold tabular-nums text-[var(--text)] ${
                          st === 'active' ? 'text-[color-mix(in_srgb,var(--accent)_90%,var(--text))]' : ''
                        }`}
                      >
                        {time}
                      </span>
                      <span className="min-w-0 flex-1 text-base font-bold leading-snug text-[var(--text)]">
                        {slot.service_name}
                        {slot.max_participants === 1 && (
                          <span className="ml-2 inline-flex items-center rounded bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-medium text-purple-600 dark:text-purple-400 border border-purple-500/25">
                            Персональная
                          </span>
                        )}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                      {slot.branch_label?.trim() || branchLabel || 'Филиал'}
                      {slot.max_participants > 0 ? (
                        <>
                          {' '}
                          · {taken}/{slot.max_participants} человек
                        </>
                      ) : null}
                    </p>
                    {slot.max_participants > 0 ? (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--bg-soft)]">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: accentHex ? hexToRgba(accentHex, 0.85) : 'var(--accent)'
                            }}
                          />
                        </div>
                        <span className="text-[11px] font-semibold tabular-nums text-[var(--text-muted)]">{pct}%</span>
                      </div>
                    ) : null}
                  </button>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-full border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-1.5 text-xs font-semibold text-[var(--text)]"
                      onClick={() => toggleExpand(slot.id)}
                    >
                      Состав группы ›
                    </button>
                    <button
                      type="button"
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                        pulse
                          ? 'animate-pulse bg-emerald-600 text-white'
                          : 'bg-[var(--accent-bg)] text-[var(--accent-fg)]'
                      }`}
                      onClick={() => toggleExpand(slot.id)}
                    >
                      Отметить ✓
                    </button>
                  </div>
                </div>
              </div>
              {isOpen ? (
                <div className="border-t border-[var(--border)] bg-[var(--bg-soft)]/60 px-3 py-3">
                  {renderSlotBookings(slot, true)}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="text-center text-xs text-[var(--text-muted)]">
        <button type="button" className="text-[var(--accent)] underline-offset-2 hover:underline" onClick={() => reload()}>
          Обновить
        </button>
      </p>
    </div>
  );
}

export function TrainerManageSlotsSection({
  heading,
  description,
  trainerMobileLayout = null,
  canStaffScheduleWrite = false
}: Props) {
  const confirmAction = useConfirm();
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const { accessToken, me, meLoading, meError, orgSlug, reloadMe } = useEmbedBootstrap();
  const {
    branchOptions,
    allOrgBranches,
    loading: branchOptionsLoading,
    error: branchOptionsError
  } = useTrainerBranchOptions(me);
  const [deletingSlotId, setDeletingSlotId] = useState<number | null>(null);
  const [deletingBookingId, setDeletingBookingId] = useState<number | null>(null);
  const [markingBookingId, setMarkingBookingId] = useState<number | null>(null);
  const [bookingsReloadKey, setBookingsReloadKey] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [requestChangeOpen, setRequestChangeOpen] = useState(false);
  const [requestCopyState, setRequestCopyState] = useState<'idle' | 'done' | 'error'>('idle');
  const [mobileWeekSelectedDate, setMobileWeekSelectedDate] = useState(() => toLocalIsoDate(new Date()));
  const [staffMode, setStaffMode] = useState<TrainerScheduleStaffDialogMode>(null);
  const [staffSlot, setStaffSlot] = useState<ScheduleSlotListItem | null>(null);

  const [branchBusy, setBranchBusy] = useState(false);
  const [branchErr, setBranchErr] = useState<string | null>(null);
  const [studioBranchFilter, setStudioBranchFilter] = useState<number | null>(null);
  const [studioSpaceFilter, setStudioSpaceFilter] = useState<TrainerStudioSpaceFilter>(null);

  const chooseBranch = async (branchId: number) => {
    const slug = orgSlug || me?.memberships?.find((m) => m.organization_id === me.organization_id)?.organization_slug || null;
    if (!slug || !accessToken) return;
    setBranchBusy(true);
    setBranchErr(null);
    try {
      const currentSession = getEmbedSession(slug);
      await postSelectTrainerBranch(branchId, accessToken);
      const fresh = await getMe(accessToken);
      setEmbedSession(slug, {
        accessToken: accessToken,
        organizationId: fresh.organization_id!,
        embedMode: 'trainer',
        refreshToken: currentSession?.refreshToken
      });
      const search = new URLSearchParams(window.location.search);
      search.set('org_slug', slug);
      navigate(`${window.location.pathname}?${search.toString()}`, { replace: true });
      await reloadMe();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearEmbedSession(slug);
        clearGlobalEmbedSession();
        window.location.reload();
        return;
      }
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка';
      setBranchErr(msg);
    } finally {
      setBranchBusy(false);
    }
  };

  const range = useMemo(() => {
    const from = new Date();
    const to = addDays(from, 14);
    return { fromDate: toIsoDate(from), toDate: toIsoDate(to) };
  }, []);

  const activeBranchFallback =
    me?.active_branch_id != null && me.active_branch_id > 0 ? me.active_branch_id : null;
  const singlePlacementBranch = branchOptions.length === 1 ? branchOptions[0]?.id ?? null : null;
  const branchId = activeBranchFallback ?? singlePlacementBranch;
  // Тренер в мобильном приложении видит сетку всех своих филиалов сразу — экран выбора филиала не нужен.
  // Админ/суперадмин в приложении тренера — все филиалы выбранной студии (ADR-0048).
  const useAllBranches = branchOptions.length > 0 && (allOrgBranches || trainerMobileLayout != null);

  const branchLabel =
    branchId != null ? branchOptions.find((b) => b.id === branchId)?.name ?? null : null;

  const orgId = me?.organization_id ?? null;
  // В режиме «все филиалы» active_branch_id может быть null: диалог открываем на филиале
  // редактируемого слота, иначе на выбранном в фильтре, иначе на первом филиале тренера.
  const slotBranchId =
    staffSlot?.branch_id != null && staffSlot.branch_id > 0 ? staffSlot.branch_id : null;
  const dialogBranchId = slotBranchId ?? branchId ?? branchOptions[0]?.id ?? null;

  function openStaffScheduleDialog(mode: TrainerScheduleStaffDialogMode, slot: ScheduleSlotListItem | null) {
    if (!mode || orgId == null) return;
    setStaffSlot(slot);
    setStaffMode(mode);
  }

  const { data: slots, loading, error, reload } = useAsync(
    () => {
      if (!accessToken) {
        return Promise.resolve([]);
      }
      if (useAllBranches) {
        return fetchTrainerManageSlotsAllBranches({
          organizationId: orgId,
          branchList: branchOptions,
          fromDate: range.fromDate,
          toDate: range.toDate,
          accessToken
        });
      }
      if (branchId == null) {
        return Promise.resolve([]);
      }
      return fetchTrainerManageSlots({
        organizationId: orgId,
        branchId,
        fromDate: range.fromDate,
        toDate: range.toDate,
        accessToken
      });
    },
    [accessToken, branchId, useAllBranches, branchOptions, range.fromDate, range.toDate, orgId]
  );

  const placementScopedSlots = useMemo(() => {
    const list = slots ?? [];
    if (!useAllBranches || branchOptions.length === 0) return list;
    const allowed = new Set(branchOptions.map((b) => b.id));
    return list.filter((slot) => slot.branch_id == null || allowed.has(slot.branch_id));
  }, [slots, useAllBranches, branchOptions]);
  const scopedSlots = useMemo(() => {
    return placementScopedSlots.filter((slot) => isTrainerOwnOrUnassignedSlot(slot, me));
  }, [placementScopedSlots, me]);
  const ownSlots = useMemo(
    () => placementScopedSlots.filter((slot) => isTrainerOwnSlot(slot, me)),
    [placementScopedSlots, me]
  );
  const [selectedDate, setSelectedDate] = useState(() => toLocalIsoDate(new Date()));
  const byDate = useMemo(() => groupByDate(scopedSlots), [scopedSlots]);
  const visibleDayEntries = useMemo(() => {
    if (!selectedDate) return [...byDate.entries()];
    const daySlots = byDate.get(selectedDate);
    return daySlots ? [[selectedDate, daySlots] as const] : [];
  }, [byDate, selectedDate]);
  const activeBookingSlots = useMemo(() => {
    if (trainerMobileLayout === 'week') {
      // В сетке раскрываются только свои занятия выбранного дня; чужие — без списка клиентов.
      return ownSlots.filter((slot) => slot.slot_date === mobileWeekSelectedDate);
    }
    return pickActiveBookingSlots(
      scopedSlots,
      trainerMobileLayout,
      visibleDayEntries,
      mobileWeekSelectedDate,
      toLocalIsoDate(new Date())
    );
  }, [trainerMobileLayout, scopedSlots, ownSlots, mobileWeekSelectedDate, visibleDayEntries]);
  const activeSlotIdsKey = useMemo(
    () => (activeBookingSlots ?? []).map((slot) => slot.id).join(','),
    [activeBookingSlots]
  );

  const {
    data: bookingsBySlot,
    loading: bookingsLoading,
    error: bookingsError,
    reload: reloadBookings
  } = useAsync(
    async () => {
      if (!accessToken || activeBookingSlots.length === 0) {
        return {} as Record<number, StaffBookingListItem[]>;
      }
      const entries = await Promise.all(
        (activeBookingSlots ?? []).map(async (slot) => [slot.id, await listSlotBookings(accessToken, slot.id, orgId)] as const)
      );
      return Object.fromEntries(entries) as Record<number, StaffBookingListItem[]>;
    },
    [accessToken, activeSlotIdsKey, bookingsReloadKey, orgId]
  );
  const requestText = useMemo(() => {
    const trainerName = me?.full_name?.trim() || me?.email || (me?.id != null ? `Тренер #${me.id}` : 'Тренер');
    const branchText = branchLabel || (useAllBranches ? 'все мои филиалы' : 'текущий филиал');
    return [
      'Запрос на изменение расписания',
      '',
      `Тренер: ${trainerName}`,
      `Филиал: ${branchText}`,
      `Период: ${formatDayRangeShort(range.fromDate, range.toDate)}`,
      '',
      'Нужно: выходной / замена / перенос слота',
      'Комментарий:'
    ].join('\n');
  }, [me, branchLabel, useAllBranches, range.fromDate, range.toDate]);

  function openRequestChange() {
    setRequestCopyState('idle');
    setRequestChangeOpen(true);
  }

  async function copyRequestText() {
    try {
      await navigator.clipboard.writeText(requestText);
      setRequestCopyState('done');
    } catch {
      setRequestCopyState('error');
    }
  }

  function openMailDraft() {
    const subject = encodeURIComponent('Запрос на изменение расписания');
    const body = encodeURIComponent(requestText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  function buildTrainerBookClientPath(slotId: number): string {
    const params = new URLSearchParams(suffix.startsWith('?') ? suffix.slice(1) : suffix);
    params.set('slot_id', String(slotId));
    return `${PATH.trainerBookClient}?${params.toString()}`;
  }

  async function onMarkAttendance(booking: StaffBookingListItem, attendance: BookingAttendance | null) {
    if (!accessToken) {
      setActionError('Нет активной сессии. Войдите снова.');
      return;
    }
    setActionError(null);
    setMarkingBookingId(booking.id);
    try {
      await setStaffBookingAttendance(accessToken, booking.id, attendance);
      setBookingsReloadKey((value) => value + 1);
    } catch (err) {
      setActionError(err instanceof ApiError && err.status === 409 ? 'Отменённую запись отметить нельзя.' : 'Не удалось сохранить отметку прихода.');
    } finally {
      setMarkingBookingId(null);
    }
  }

  async function onDeleteBooking(booking: StaffBookingListItem) {
    if (!accessToken) {
      setActionError('Нет активной сессии. Войдите снова.');
      return;
    }
    const confirmed = await confirmAction({
      title: 'Снять клиента с записи?',
      message: bookingClientLabel(booking),
      confirmLabel: 'Снять',
      danger: true
    });
    if (!confirmed) return;
    setActionError(null);
    setDeletingBookingId(booking.id);
    try {
      await deleteStaffBooking(accessToken, booking.id);
      setBookingsReloadKey((value) => value + 1);
      reload();
      reloadBookings();
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(err.detail);
      } else {
        setActionError(err instanceof Error ? err.message : 'Не удалось снять клиента с записи');
      }
    } finally {
      setDeletingBookingId(null);
    }
  }

  function renderSlotBookings(slot: ScheduleSlotListItem, compact = false) {
    const items = bookingsBySlot?.[slot.id] ?? [];
    const canAddClient = slot.places_left > 0 && slot.is_available !== false;
    if (bookingsLoading && !items.length) {
      return <p className="text-xs text-[var(--text-muted)]">Загружаем клиентов...</p>;
    }
    if (!items.length) {
      return (
        <div className="space-y-2">
          <p className="text-xs text-[var(--text-muted)]">Пока никто не записан.</p>
          {canAddClient ? (
            <button
              type="button"
              className="rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-1.5 text-xs font-semibold text-[var(--text)]"
              onClick={() => navigate(buildTrainerBookClientPath(slot.id))}
            >
              Записать клиента
            </button>
          ) : null}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <ul className="space-y-2">
          {items.map((booking) => (
            <li
              key={booking.id}
              className={`rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2.5 ${
                compact ? '' : 'shadow-sm'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-[var(--text)]">{bookingClientLabel(booking)}</p>
                  {booking.comment ? (
                    <p className="mt-1 text-xs leading-snug text-[var(--text-muted)]">{booking.comment}</p>
                  ) : null}
                  {booking.represented_minor_name ? (
                    <p className="mt-1 text-xs text-[var(--text-muted)]">Ребёнок: {booking.represented_minor_name}</p>
                  ) : null}
                  {booking.qr_entered_at ? (
                    <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                      Прошёл по QR в {formatClockTime(booking.qr_entered_at)}
                    </p>
                  ) : null}
                </div>
                {booking.client_phone ? (
                  <div className="flex shrink-0 gap-1.5">
                    <a
                      href={telHref(booking.client_phone)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] text-[var(--text)]"
                      aria-label={`Позвонить: ${formatPhone(booking.client_phone)}`}
                    >
                      <Phone className="h-4 w-4" aria-hidden />
                    </a>
                    <a
                      href={telHref(booking.client_phone).replace(/^tel:/, 'sms:')}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] text-[var(--text)]"
                      aria-label={`Написать сообщение: ${formatPhone(booking.client_phone)}`}
                    >
                      <MessageSquare className="h-4 w-4" aria-hidden />
                    </a>
                  </div>
                ) : null}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <div className="inline-flex overflow-hidden rounded-full border border-[var(--border)]" role="group" aria-label="Приход клиента">
                  {(['attended', 'no_show'] as const).map((value) => {
                    const active = booking.attendance === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={active}
                        disabled={markingBookingId === booking.id}
                        onClick={() => void onMarkAttendance(booking, active ? null : value)}
                        className={`px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${
                          active
                            ? value === 'attended'
                              ? 'bg-emerald-600 text-white'
                              : 'bg-[var(--error-text)] text-white'
                            : 'bg-[var(--bg-elevated)] text-[var(--text)]'
                        }`}
                      >
                        {value === 'attended' ? 'Пришёл' : 'Не пришёл'}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="ml-auto rounded-full px-3 py-1.5 text-xs font-semibold text-[var(--error-text)] disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={deletingBookingId === booking.id}
                  onClick={() => void onDeleteBooking(booking)}
                >
                  {deletingBookingId === booking.id ? 'Снимаем…' : 'Снять с записи'}
                </button>
              </div>
            </li>
          ))}
        </ul>
        {canAddClient ? (
          <button
            type="button"
            className="rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-1.5 text-xs font-semibold text-[var(--text)]"
            onClick={() => navigate(buildTrainerBookClientPath(slot.id))}
          >
            Записать клиента
          </button>
        ) : null}
      </div>
    );
  }

  async function onDeleteSlot(slot: ScheduleSlotListItem) {
    if (!accessToken) {
      setActionError('Нет активной сессии. Войдите снова.');
      return;
    }
    const confirmed = await confirmAction({
      title: 'Удалить занятие?',
      message: `«${slot.service_name}» ${formatSlotDayShort(slot.slot_date)} ${(slot.start_time || '').slice(0, 5)}`,
      confirmLabel: 'Удалить',
      danger: true
    });
    if (!confirmed) return;
    setActionError(null);
    setDeletingSlotId(slot.id);
    try {
      await deleteScheduleSlot(slot.id, accessToken);
      reload();
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(err.detail);
      } else {
        setActionError(err instanceof Error ? err.message : 'Не удалось удалить слот');
      }
    } finally {
      setDeletingSlotId(null);
    }
  }

  const shellClass = trainerMobileLayout
    ? 'space-y-3'
    : 'space-y-4 w-full min-w-0';
  const detailsError = actionError ?? bookingsError;

  if (meLoading || !me) {
    return (
      <div className={trainerMobileLayout ? 'space-y-3' : shellClass}>
        <div className="h-7 w-44 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-[var(--border)] px-3 py-2">
              <div className="space-y-1">
                <div className="h-4 w-36 animate-pulse rounded bg-[var(--bg-soft)]" />
                <div className="h-3 w-24 animate-pulse rounded bg-[var(--bg-soft)]" />
              </div>
              <div className="h-7 w-16 animate-pulse rounded-xl bg-[var(--bg-soft)]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (meError) {
    return (
      <div className={shellClass}>
        {!trainerMobileLayout ? <h1 className="text-xl font-semibold tracking-tight">{heading}</h1> : null}
        <EmbedMessageBlock variant="error" size="field">{meError}</EmbedMessageBlock>
      </div>
    );
  }

  if (allOrgBranches && branchOptionsLoading) {
    return (
      <div className={shellClass}>
        <p className="text-sm text-[var(--text-muted)]">Загрузка филиалов…</p>
      </div>
    );
  }

  if (allOrgBranches && branchOptionsError) {
    return (
      <div className={shellClass}>
        <EmbedMessageBlock variant="error" size="field">{branchOptionsError}</EmbedMessageBlock>
      </div>
    );
  }

  if (!useAllBranches && branchId == null) {
    const placements = me?.trainer_placements ?? [];
    return (
      <div className={shellClass}>
        {!trainerMobileLayout ? <h1 className="text-xl font-semibold tracking-tight">{heading}</h1> : null}
        <p className="text-sm text-[var(--text-muted)] mb-4">
          Выберите филиал для работы — без него расписание не загрузится.
        </p>
        {placements.length > 0 ? (
          <div className="flex flex-col gap-2.5 max-w-md w-full">
            {placements.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => void chooseBranch(p.branch_id)}
                disabled={branchBusy}
                className="flex h-12 w-full items-center justify-center rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-4 text-sm font-semibold text-[var(--text)] transition-all hover:border-[var(--accent)] hover:bg-[var(--bg-soft)] active:scale-[0.98] disabled:opacity-60 cursor-pointer"
              >
                {p.branch_name}
              </button>
            ))}
            {branchBusy && <p className="text-xs text-[var(--text-muted)] mt-1">Сохраняем…</p>}
            {branchErr && <p className="text-xs text-[var(--error-text)] mt-1">{branchErr}</p>}
          </div>
        ) : (
          <p className="text-sm text-[var(--text-muted)]">У вас нет доступных филиалов.</p>
        )}
      </div>
    );
  }

  if (trainerMobileLayout === 'today') {
    if (loading) {
      return (
        <div className={shellClass}>
          <p className="text-sm text-[var(--text-muted)]">Загрузка…</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className={shellClass}>
          <EmbedMessageBlock variant="error" size="field">{error}</EmbedMessageBlock>
        </div>
      );
    }
    return (
        <div className={shellClass}>
        <TrainerMobileTodayLayout slots={scopedSlots} suffix={suffix} branchLabel={branchLabel} reload={reload} renderSlotBookings={renderSlotBookings} />
      </div>
    );
  }

  if (trainerMobileLayout === 'week') {
    if (loading) {
      return (
        <div className={shellClass}>
          <p className="text-sm text-[var(--text-muted)]">Загрузка…</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className={shellClass}>
          <EmbedMessageBlock variant="error" size="field">{error}</EmbedMessageBlock>
        </div>
      );
    }
    return (
      <div className={shellClass}>
        <TrainerStudioGrid
          slots={placementScopedSlots}
          rangeFrom={range.fromDate}
          rangeTo={range.toDate}
          selectedIso={mobileWeekSelectedDate}
          onSelectedIsoChange={setMobileWeekSelectedDate}
          me={me}
          branchOptions={branchOptions}
          branchFilter={studioBranchFilter}
          spaceFilter={studioSpaceFilter}
          onBranchFilterChange={(id) => {
            setStudioBranchFilter(id);
            setStudioSpaceFilter(null);
          }}
          onSpaceFilterChange={setStudioSpaceFilter}
          renderOwnBookings={(slot) => renderSlotBookings(slot, true)}
          onRequestChange={openRequestChange}
          reload={reload}
        />
      </div>
    );
  }

  return (
    <div className={shellClass}>
      {!trainerMobileLayout ? (
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">{heading}</h1>
          <p className="text-sm text-[var(--text-muted)]">{description}</p>
          {!canStaffScheduleWrite ? (
            <p className="mt-2 rounded-md border border-dashed border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 text-xs text-[var(--text-muted)]">
              Создавать, менять и удалять занятия можно после того, как администратор выдаст право на работу с
              расписанием.
            </p>
          ) : null}
          {canStaffScheduleWrite && orgId != null && !trainerMobileLayout ? (
            <button
              type="button"
              className="mt-2 rounded-lg border border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] px-3 py-2 text-sm font-semibold text-[var(--accent)]"
              onClick={() => openStaffScheduleDialog('create', null)}
            >
              Добавить слот
            </button>
          ) : null}
        </div>
      ) : null}
      {loading ? (
        <div className="space-y-6">
          {Array.from({ length: 2 }).map((_, di) => (
            <section key={di}>
              <div className="mb-2 h-3.5 w-32 animate-pulse rounded bg-[var(--bg-soft)]" />
              <ul className="space-y-2">
                {Array.from({ length: di === 0 ? 3 : 2 }).map((_, si) => (
                  <li key={si} className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2">
                    <div className="space-y-1">
                      <div className="h-4 w-36 animate-pulse rounded bg-[var(--bg-elevated)]" />
                      <div className="h-3 w-24 animate-pulse rounded bg-[var(--bg-elevated)]" />
                    </div>
                    <div className="h-7 w-16 animate-pulse rounded-sm bg-[var(--bg-elevated)]" />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : error ? (
        <EmbedMessageBlock variant="error" size="field">{error}</EmbedMessageBlock>
      ) : byDate.size === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Нет слотов в выбранном периоде.</p>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-3">
            <label className="min-w-[13rem] text-sm text-[var(--text-muted)]">
              <span className="mb-1 block">Дата расписания</span>
              <input
                type="date"
                className="h-10 w-full rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm text-[var(--text)]"
                min={range.fromDate}
                max={range.toDate}
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm font-medium text-[var(--text)]"
              onClick={() => setSelectedDate(toLocalIsoDate(new Date()))}
            >
              Сегодня
            </button>
          </div>
          {visibleDayEntries.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">На выбранную дату слотов нет.</p>
          ) : null}
          {visibleDayEntries.map(([day, daySlots]) => (
            <section key={day}>
              <h2 className="mb-2 text-sm font-medium text-[var(--text-muted)]">{formatDayLabel(day)}</h2>
              <ul className="space-y-2">
                {daySlots.map((slot) => {
                  const time = `${(slot.start_time || '').slice(0, 5)} — ${(slot.end_time || '').slice(0, 5)}`.trim();
                  return (
                    <li
                      key={slot.id}
                      className="flex flex-col gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <span className="font-medium text-[var(--text)]">
                          {slot.service_name}
                          {slot.max_participants === 1 && (
                            <span className="ml-2 inline-flex items-center rounded bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-medium text-purple-600 dark:text-purple-400 border border-purple-500/25">
                              Персональная
                            </span>
                          )}
                        </span>
                        {slot.trainer_name ? (
                          <span className="text-[var(--text-muted)]"> — {slot.trainer_name}</span>
                        ) : null}
                      </div>
                        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 text-[var(--text-muted)]">
                          {time}
                          {slot.max_participants > 0 ? (
                            <span className="ml-2">
                              (занято {slot.max_participants - slot.places_left} / {slot.max_participants})
                            </span>
                          ) : null}
                          <div className="mt-3">{renderSlotBookings(slot, true)}</div>
                        </div>
                        {canStaffScheduleWrite ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded-sm border border-[var(--border)] px-2 py-1 text-[12px] text-[var(--text)]"
                              onClick={() => openStaffScheduleDialog('edit', slot)}
                            >
                              Изменить
                            </button>
                            <button
                              type="button"
                              className="rounded-sm border border-[var(--border)] px-2 py-1 text-[12px] text-[var(--text)]"
                              onClick={() => openStaffScheduleDialog('move', slot)}
                            >
                              Перенос
                            </button>
                            <button
                              type="button"
                              className="rounded-sm border border-[var(--border)] px-2 py-1 text-[12px] text-[var(--text)]"
                              onClick={() => openStaffScheduleDialog('notify', slot)}
                            >
                              Уведомить
                            </button>
                            <button
                              type="button"
                              className="rounded-sm border border-[color-mix(in_srgb,var(--error-text)_40%,transparent)] px-2 py-1 text-[13px] text-[var(--error-text)] transition-colors hover:bg-[color-mix(in_srgb,var(--error-text)_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={deletingSlotId === slot.id}
                              onClick={() => onDeleteSlot(slot)}
                            >
                              {deletingSlotId === slot.id ? 'Удаляем…' : 'Удалить'}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
      {detailsError ? <EmbedMessageBlock variant="error" size="field">{detailsError}</EmbedMessageBlock> : null}
      {canStaffScheduleWrite && orgId != null && accessToken && dialogBranchId != null ? (
        <TrainerScheduleStaffDialog
          mode={staffMode}
          onClose={() => {
            setStaffMode(null);
            setStaffSlot(null);
          }}
          accessToken={accessToken}
          organizationId={orgId}
          branchId={dialogBranchId}
          trainerUserId={me?.id ?? null}
          slot={staffSlot}
          onCommitted={() => reload()}
        />
      ) : null}
      {requestChangeOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="trainer-request-change-title"
            className="w-full max-w-lg rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="trainer-request-change-title" className="text-lg font-semibold text-[var(--text)]">
                  Запрос на изменение
                </h2>
                <p className="mt-1 text-sm text-[var(--text-muted)]">
                  Подготовили текст. Его можно отправить администратору любым удобным каналом.
                </p>
              </div>
              <button
                type="button"
                className="rounded-full px-2 py-1 text-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)]"
                onClick={() => setRequestChangeOpen(false)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>
            <textarea
              className="mt-4 min-h-44 w-full rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--text)]"
              readOnly
              value={requestText}
            />
            {requestCopyState === 'done' ? (
              <EmbedMessageBlock className="mt-3" variant="success" size="field" role="status">
                Текст скопирован.
              </EmbedMessageBlock>
            ) : null}
            {requestCopyState === 'error' ? (
              <EmbedMessageBlock className="mt-3" variant="error" size="field">
                Не получилось скопировать автоматически. Текст можно выделить вручную.
              </EmbedMessageBlock>
            ) : null}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-xl bg-[var(--accent-bg)] px-4 py-2 text-sm font-semibold text-[var(--accent-fg)]"
                onClick={() => void copyRequestText()}
              >
                Скопировать
              </button>
              <button
                type="button"
                className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-semibold text-[var(--text)] hover:bg-[var(--bg-soft)]"
                onClick={openMailDraft}
              >
                Открыть почту
              </button>
              <button
                type="button"
                className="rounded-xl border border-transparent px-4 py-2 text-sm font-medium text-[var(--text-muted)] hover:bg-[var(--bg-soft)]"
                onClick={() => setRequestChangeOpen(false)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {!trainerMobileLayout ? (
        <p className="text-xs text-[var(--text-muted)]">
          Период: {formatDayRangeShort(range.fromDate, range.toDate)}.{' '}
          <button
            type="button"
            className="text-[var(--accent)] underline-offset-2 hover:underline"
            onClick={() => reload()}
          >
            Обновить
          </button>
        </p>
      ) : null}
    </div>
  );
}
